import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { FREE_SHIPPING_THRESHOLD, SHIPPING_FLAT_RATE } from '../lib/shop';
import { cartQuantity, restoreCart, removeOrderedItems } from '../utils/cart';
import { readSession, writeSession } from '../utils/checkout';
import { isMadeToOrder } from '../utils/orderPresentation';
import { withCharacterName } from '../lib/characters';

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const saved = localStorage.getItem('lpad_cart');
      const stored = saved ? JSON.parse(saved) : [];
      return restoreCart(stored).map(item => withCharacterName(item));
    } catch { return []; }
  });
  const [isOpen, setIsOpen] = useState(false);
  const savedSessions = readSession('lpad_paid_sessions', []);
  const completedSessions = useRef(new Set(Array.isArray(savedSessions) ? savedSessions : []));

  useEffect(() => {
    try { localStorage.setItem('lpad_cart', JSON.stringify(items)); } catch { }
  }, [items]);

  const addItem = useCallback((product, color = '', quantity = 1, metadata = {}) => {
    if (!product?.slug || !Number.isFinite(product.price) || product.price < 0) return;
    const itemType = { madeToOrder: product.made_to_order === true, configurationId: metadata.configurationId, configuration: metadata.configuration };
    quantity = cartQuantity(itemType, quantity);
    if (!quantity) return;
    setItems(prev => {
      const cartKey = metadata.cartKey || metadata.configurationId || `${product.slug}-${color}`;
      const matches = i => i.cartKey === cartKey || (!i.cartKey && `${i.slug}-${i.color}` === cartKey)
        || (!isMadeToOrder(itemType) && !isMadeToOrder(i) && i.slug === product.slug);
      const existing = prev.find(matches);
      // Pièce unique du registre : il n'en existe qu'une, la quantité ne dépasse jamais 1.
      const isUniquePiece = product.made_to_order !== true && !metadata.configurationId && !metadata.configuration;
      if (existing) {
        return prev.map(i => matches(i)
          ? { ...i, quantity: isUniquePiece ? 1 : i.quantity + quantity }
          : i);
      }
      return [...prev, withCharacterName({
        cartKey,
        slug: product.slug,
        name: product.name,
        price: product.price,
        color,
        quantity,
        image: metadata.image || product.images?.[0] || '',
        has_photos: product.has_photos,
        character: product.character,
        placeholder_color: product.placeholder_color,
        madeToOrder: product.made_to_order === true,
        configurationId: metadata.configurationId || null,
        configuration: metadata.configuration || null,
      })];
    });
    setIsOpen(true);
  }, []);

  const removeItem = useCallback((slug, color, cartKey = null) => {
    setItems(prev => prev.filter(i => {
      if (cartKey) return i.cartKey !== cartKey;
      return !(i.slug === slug && i.color === color);
    }));
  }, []);

  const updateQty = useCallback((slug, color, qty, cartKey = null) => {
    if (!Number.isSafeInteger(qty)) return;
    if (qty <= 0) { removeItem(slug, color, cartKey); return; }
    setItems(prev => prev.map(i => {
      const matches = cartKey ? i.cartKey === cartKey : i.slug === slug && i.color === color;
      return matches ? { ...i, quantity: cartQuantity(i, qty) } : i;
    }));
  }, [removeItem]);

  const clearCart = useCallback(() => setItems([]), []);
  const completeOrder = useCallback((order, sessionId) => {
    if (!sessionId || !Array.isArray(order?.items) || completedSessions.current.has(sessionId)) return;
    completedSessions.current.add(sessionId);
    writeSession('lpad_paid_sessions', [...completedSessions.current]);
    setItems(prev => removeOrderedItems(prev, order.items));
    // Une autre session en cours conserve ses informations de livraison.
    if (readSession('lpad_checkout_pending')?.sessionId === sessionId) {
      writeSession('lpad_checkout_pending', null);
      writeSession('lpad_checkout_form', null);
    }
  }, []);

  const total = items.reduce((sum, i) => sum + i.price * i.quantity, 0);
  const count = items.reduce((sum, i) => sum + i.quantity, 0);
  const shipping = items.length === 0 || total >= FREE_SHIPPING_THRESHOLD ? 0 : SHIPPING_FLAT_RATE;
  const grandTotal = total + shipping;

  return (
    <CartContext.Provider value={{ items, addItem, removeItem, updateQty, clearCart, completeOrder, total, count, shipping, grandTotal, isOpen, setIsOpen }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
